import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Upload, Trash2, Loader2, Info } from 'lucide-react';
import type {
  ProjectFileStream,
  ProjectFileStreamKind,
  ProjectFileKindTag,
  ProjectQuoteType,
} from '../../types';
import {
  GENERAL_BATCH_FILE_ACCEPT,
  VERSION_BATCH_FILE_ACCEPT,
  fileMatchesStreamKind,
  kindTagOptionsForFile,
} from '../../utils/projectFileService';
import {
  PROJECT_FILE_DETAIL_STAGE_LABELS,
  PROJECT_FILE_DETAIL_STAGES,
} from '../../utils/projectFileVersionGroups';
import {
  dispositionsForVersionUploadRows,
  emptyGeneralUploadRow,
  emptyQuoteUploadRow,
  emptyVersionUploadRowUnified,
  QUOTE_TYPE_OPTIONS,
  trackKindShort,
  versionRowStreamKind,
  type ProjectUploadModalKind,
  type ProjectUploadRow,
} from './projectUploadModalShared';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { defaultCncStructureIdsForTrack } from '../../utils/cncModelStructures';
import { writableProjectFolders } from '../../utils/projectFileFolders';
import { useAuth } from '../../utils/AuthContext';
import { canSeeProjectFinancials } from '../../utils/userRoles';
import { STRUCTURES_LOCKED_HINT } from '../../utils/projectStructureGate';
import { projectIsSplitIntoStructures } from '../../utils/projectFileStructureTracks';
import { uploadStructureGroups } from '../../utils/projectFileStreamGroups';
import StructureCheckDropdown from './StructureCheckDropdown';
import './ProjectFiles.css';

/** New-track TYPE field: fixed set and display order (no legacy `general` option). */
const VERSION_TRACK_TYPES_ORDERED = [
  'three_d_html',
  'three_d_raw',
  'timber_list_csv',
  'hardware_list_csv',
  'two_d_pdf',
] as const satisfies readonly ProjectFileStreamKind[];

function LodHelp() {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const place = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 320;
    const tipHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const openUp = spaceBelow < tipHeight && rect.top > spaceBelow;
    setPosition({
      top: openUp ? Math.max(12, rect.top - tipHeight - 8) : rect.bottom + 8,
      left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
    });
  };

  const show = () => {
    if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    place();
    setOpen(true);
  };

  const hide = () => {
    hideTimerRef.current = window.setTimeout(() => setOpen(false), 160);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="pf-lod-info-btn"
        aria-label="About level of detail"
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <Info size={12} aria-hidden />
      </button>
      {open
        ? createPortal(
            <div
              className="pf-lod-tip"
              role="tooltip"
              style={{ top: position.top, left: position.left }}
              onMouseEnter={show}
              onMouseLeave={hide}
            >
              <p>
                Level of detail is how far this file has progressed. The number after the dot is the designer’s own tracking number. The version label does not have to use that exact format.
              </p>
              <ul>
                <li>Conceptual — simple, model only, no joinery (for example LOD 100.x)</li>
                <li>Engineer Review — slightly more detailed and thought through for engineering (for example LOD 200.x)</li>
                <li>Joinery — LOD 300.x</li>
                <li>Supplementary details — fasteners, hardware, and structural components included in the model and drawings (for example LOD 400)</li>
              </ul>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

const VERSION_TRACK_TYPE_LABEL: Record<(typeof VERSION_TRACK_TYPES_ORDERED)[number], string> = {
  three_d_html: 'HTML',
  three_d_raw: '3D',
  timber_list_csv: 'Timber List',
  hardware_list_csv: 'Hardware',
  two_d_pdf: '2D Drawings',
};

export interface ProjectUploadModalDialogProps {
  streams: ProjectFileStream[];
  /** Default stream for new rows (Files tab selection or null). */
  hintStreamId: string | null;
  uploadModalKind: ProjectUploadModalKind;
  onUploadModalKindChange: (kind: ProjectUploadModalKind) => void;
  uploadRows: ProjectUploadRow[];
  setUploadRows: React.Dispatch<React.SetStateAction<ProjectUploadRow[]>>;
  appendUploadFiles: (files: File[]) => void;
  uploading: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  proposalStructures: StructureRef[];
  /** False until the project has its first timber list — structure pickers stay hidden. */
  structuresReady?: boolean;
}

const ProjectUploadModalDialog: React.FC<ProjectUploadModalDialogProps> = ({
  streams,
  hintStreamId,
  uploadModalKind,
  onUploadModalKindChange,
  uploadRows,
  setUploadRows,
  appendUploadFiles,
  uploading,
  onCancel,
  onSubmit,
  proposalStructures,
  structuresReady = false,
}) => {
  const { user } = useAuth();
  const folderOptions = useMemo(
    () => writableProjectFolders(canSeeProjectFinancials(user)),
    [user],
  );
  const uploadBatchFileInputRef = useRef<HTMLInputElement>(null);
  const uploadModalDropDepthRef = useRef(0);
  const uploadDropzoneIgnoreClickRef = useRef(false);
  const [uploadModalDropOver, setUploadModalDropOver] = useState(false);

  useEffect(() => {
    uploadModalDropDepthRef.current = 0;
    uploadDropzoneIgnoreClickRef.current = false;
    setUploadModalDropOver(false);
  }, []);

  const visibleRows = uploadRows.filter(r => r.rowKind === uploadModalKind);
  const visibleCount = visibleRows.length;

  const versionDispositionById = useMemo(
    () => dispositionsForVersionUploadRows(uploadRows, streams),
    [uploadRows, streams],
  );
  const projectSplit = useMemo(() => projectIsSplitIntoStructures(streams), [streams]);
  const structureGroups = useMemo(
    () => (projectSplit ? uploadStructureGroups(streams, proposalStructures) : []),
    [projectSplit, streams, proposalStructures],
  );
  const pickStructureGroups = structureGroups.length > 0;

  return (
    <div className="pf-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="pf-upload-modal-title">
      <div className="pf-modal pf-modal--version pf-modal--version-batch">
        <h3 id="pf-upload-modal-title" className="pf-modal-title">
          Upload files
        </h3>

        <div className="pf-upload-mode-tabs" role="tablist" aria-label="Upload type">
          <button
            type="button"
            role="tab"
            aria-selected={uploadModalKind === 'version'}
            className={`pf-upload-mode-tab${uploadModalKind === 'version' ? ' pf-upload-mode-tab--active' : ''}`}
            disabled={uploading}
            onClick={() => onUploadModalKindChange('version')}
          >
            <div className="pf-upload-mode-tab-title">Version control</div>
            <p className="pf-upload-mode-tab-desc">
              For drawing PDFs, HTML or 3D viewers, <strong>3D models</strong>{' '}
              (Cadwork, SketchUp, IFC, STEP, DXF, SAT), and timber CSV — adds <strong>revisions</strong> to a
              named history (Rev labels, tracks).
            </p>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={uploadModalKind === 'general'}
            className={`pf-upload-mode-tab${uploadModalKind === 'general' ? ' pf-upload-mode-tab--active' : ''}`}
            disabled={uploading}
            onClick={() => onUploadModalKindChange('general')}
          >
            <div className="pf-upload-mode-tab-title">General document</div>
            <p className="pf-upload-mode-tab-desc">
              For photos, scans, specifications, Cadwork .cwlm, spreadsheets, and any other file that doesn’t need a formal version history line.
            </p>
          </button>
        </div>

        <p className="pf-modal-hint pf-modal-hint--calm pf-upload-modal-footnote">
          {uploadModalKind === 'version'
            ? projectSplit
              ? 'Drag files in or add rows — each card is one release. Whole project uploads one shared file. Otherwise pick a structure group.'
              : 'Drag files in or add rows — each card is one release (label required; tracks are inferred).'
            : 'Drag files in or add rows — for CSV/HTML you can set the file kind (timber list / HTML) where shown.'}
        </p>

        <div
          role="button"
          tabIndex={0}
          className={`pf-version-dropzone${uploadModalDropOver ? ' pf-version-dropzone--active' : ''}`}
          onClick={() => {
            if (uploadDropzoneIgnoreClickRef.current) return;
            uploadBatchFileInputRef.current?.click();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!uploadDropzoneIgnoreClickRef.current) uploadBatchFileInputRef.current?.click();
            }
          }}
          onDragEnter={e => {
            e.preventDefault();
            e.stopPropagation();
            uploadModalDropDepthRef.current += 1;
            setUploadModalDropOver(true);
          }}
          onDragLeave={e => {
            e.preventDefault();
            e.stopPropagation();
            uploadModalDropDepthRef.current -= 1;
            if (uploadModalDropDepthRef.current <= 0) {
              uploadModalDropDepthRef.current = 0;
              setUploadModalDropOver(false);
            }
          }}
          onDragOver={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={e => {
            e.preventDefault();
            e.stopPropagation();
            uploadModalDropDepthRef.current = 0;
            setUploadModalDropOver(false);
            uploadDropzoneIgnoreClickRef.current = true;
            window.setTimeout(() => {
              uploadDropzoneIgnoreClickRef.current = false;
            }, 200);
            appendUploadFiles(Array.from(e.dataTransfer.files || []));
          }}
        >
          <Upload size={22} strokeWidth={2} aria-hidden />
          <div className="pf-version-dropzone-text">
            <strong>Drag files here</strong>
            <span>
              {uploadModalKind === 'version'
                ? 'PDF, HTML, 3D (Cadwork, SketchUp .skp, IFC, STEP, DXF, SAT), CSV — matched to version tracks below.'
                : 'Any file type — photos, PDFs, Cadwork .cwlm, Office docs, etc.'}
            </span>
          </div>
        </div>

        <input
          key={uploadModalKind}
          ref={uploadBatchFileInputRef}
          type="file"
          multiple
          accept={
            uploadModalKind === 'version'
              ? VERSION_BATCH_FILE_ACCEPT
              : GENERAL_BATCH_FILE_ACCEPT
          }
          style={{ display: 'none' }}
          onChange={e => {
            const list = Array.from(e.target.files || []);
            e.target.value = '';
            appendUploadFiles(list);
          }}
        />

        <div className="pf-version-rows-wrap">
          <div className="pf-version-rows" role="list">
            {visibleRows.map(row => {
              if (row.rowKind === 'quote') {
                return (
                  <article key={row.clientId} className="pf-version-row-card pf-version-row-card--quote" role="listitem">
                    <div className="pf-version-row-line pf-version-row-line--top pf-version-row-line--single">
                      <div className="pf-vr-cell pf-vr-cell--file pf-vr-cell--grow">
                        <span className="pf-vr-mu">File</span>
                        <span className="pf-version-row-filename pf-version-row-filename--title" title={row.file?.name || undefined}>
                          {row.file?.name ?? 'No file yet'}
                        </span>
                      </div>
                    </div>
                    <div className="pf-version-row-line pf-version-row-line--bottom">
                      <div className="pf-vr-cell pf-vr-cell--tag">
                        <span className="pf-vr-mu">Quote type</span>
                        <select
                          className="pf-tag-select pf-text-input-height pf-vr-input"
                          value={row.quoteType}
                          onChange={e => {
                            const v = e.target.value as ProjectQuoteType;
                            setUploadRows(prev =>
                              prev.map(r =>
                                r.clientId === row.clientId && r.rowKind === 'quote' ? { ...r, quoteType: v } : r,
                              ),
                            );
                          }}
                          aria-label="Quote type"
                        >
                          {QUOTE_TYPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {uploadModalKind === 'quote' && visibleCount > 1 ? (
                        <button
                          type="button"
                          className="pf-icon-btn pf-icon-danger pf-version-row-remove pf-vr-cell-remove"
                          title="Remove"
                          aria-label="Remove row"
                          disabled={uploading}
                          onClick={() =>
                            setUploadRows(prev => {
                              const next = prev.filter(r => r.clientId !== row.clientId);
                              const quoteLeft = next.filter(r => r.rowKind === 'quote');
                              if (quoteLeft.length === 0) return [emptyQuoteUploadRow()];
                              return next;
                            })
                          }
                        >
                          <Trash2 size={15} />
                        </button>
                      ) : (
                        <span className="pf-version-row-remove-placeholder pf-vr-cell-remove" aria-hidden />
                      )}
                    </div>
                  </article>
                );
              }

              if (row.rowKind === 'general') {
                const tagTarget = row.file ?? { name: '', type: '' };
                const opts = kindTagOptionsForFile(tagTarget);
                const safeTag = opts.some(o => o.value === row.kindTag) ? row.kindTag : 'none';
                return (
                  <article key={row.clientId} className="pf-version-row-card pf-version-row-card--general" role="listitem">
                    <div className="pf-version-row-line pf-version-row-line--top pf-version-row-line--single">
                      <div className="pf-vr-cell pf-vr-cell--file pf-vr-cell--grow">
                        <span className="pf-vr-mu">File</span>
                        <span className="pf-version-row-filename pf-version-row-filename--title" title={row.file?.name || undefined}>
                          {row.file?.name ?? 'No file yet'}
                        </span>
                      </div>
                    </div>
                    <div className="pf-version-row-line pf-version-row-line--bottom">
                      <div className="pf-vr-cell pf-vr-cell--tag">
                        <span className="pf-vr-mu">Kind</span>
                        <select
                          className="pf-tag-select pf-text-input-height pf-vr-input"
                          value={safeTag}
                          onChange={e => {
                            const v = e.target.value as ProjectFileKindTag;
                            setUploadRows(prev =>
                              prev.map(r =>
                                r.clientId === row.clientId && r.rowKind === 'general' ? { ...r, kindTag: v } : r,
                              ),
                            );
                          }}
                          aria-label="File kind"
                          title="Timber list / HTML for integrations"
                        >
                          {opts.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="pf-vr-cell pf-vr-cell--grow pf-vr-cell--folder">
                        <span className="pf-vr-mu">Folder</span>
                        <select
                          className="pf-tag-select pf-text-input-height pf-vr-input"
                          value={row.folderPath}
                          disabled={uploading}
                          onChange={e => {
                            const v = e.target.value;
                            setUploadRows(prev =>
                              prev.map(r =>
                                r.clientId === row.clientId && r.rowKind === 'general' ? { ...r, folderPath: v } : r,
                              ),
                            );
                          }}
                          aria-label="Project folder"
                          title="Where this file is filed in Project folders"
                        >
                          <option value="">Unsorted</option>
                          {folderOptions.map(f => (
                            <option key={f.path} value={f.path}>
                              {f.path.includes('/') ? `${f.path.split('/').length > 1 ? '— ' : ''}${f.label}` : f.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {uploadModalKind === 'general' && visibleCount > 1 ? (
                        <button
                          type="button"
                          className="pf-icon-btn pf-icon-danger pf-version-row-remove pf-vr-cell-remove"
                          title="Remove"
                          aria-label="Remove row"
                          disabled={uploading}
                          onClick={() =>
                            setUploadRows(prev => {
                              const next = prev.filter(r => r.clientId !== row.clientId);
                              const generalLeft = next.filter(r => r.rowKind === 'general');
                              if (generalLeft.length === 0) return [emptyGeneralUploadRow()];
                              return next;
                            })
                          }
                        >
                          <Trash2 size={15} />
                        </button>
                      ) : (
                        <span className="pf-version-row-remove-placeholder pf-vr-cell-remove" aria-hidden />
                      )}
                    </div>
                  </article>
                );
              }

              const disposition = versionDispositionById.get(row.clientId) ?? { kind: 'empty' as const };
              const historyStreams =
                disposition.kind === 'matchedExisting' ? disposition.streams : [];

              const matchedKinds = row.file
                ? VERSION_TRACK_TYPES_ORDERED.filter(k =>
                    fileMatchesStreamKind(row.file!.type || '', row.file!.name, k),
                  )
                : [...VERSION_TRACK_TYPES_ORDERED];
              const kindOptions: ProjectFileStreamKind[] =
                row.file && matchedKinds.length > 0 ? [...matchedKinds] : [...VERSION_TRACK_TYPES_ORDERED];

              const showHistorySelect = disposition.kind === 'matchedExisting' && historyStreams.length > 1;
              const showHistoryReadonly = disposition.kind === 'matchedExisting' && historyStreams.length === 1;
              const showNewTrackFields =
                disposition.kind === 'newTrackLeader' || disposition.kind === 'empty';
              const showBatchFollow = disposition.kind === 'newTrackFollow';
              const isThreeD = versionRowStreamKind(row, disposition) === 'three_d_raw';
              const isTimberList = versionRowStreamKind(row, disposition) === 'timber_list_csv';
              // Structures a "For CNC" tick should preselect: the target track's tags once the
              // project is split (existing history), or the tags chosen for a brand-new track.
              const cncDefaultStructureIds = (): string[] => {
                const proposalIds = proposalStructures.map(s => s.id);
                if (disposition.kind === 'matchedExisting') {
                  const picked =
                    historyStreams.find(s => s.id === row.pickStreamId)
                    ?? (hintStreamId ? historyStreams.find(s => s.id === hintStreamId) : undefined)
                    ?? historyStreams[0];
                  return defaultCncStructureIdsForTrack(picked?.structureIds, proposalIds);
                }
                if (disposition.kind === 'newTrackFollow') {
                  const leader = uploadRows.find(
                    (r): r is ProjectUploadRow & { rowKind: 'version' } =>
                      r.rowKind === 'version'
                      && r.clientId !== row.clientId
                      && versionDispositionById.get(r.clientId)?.kind === 'newTrackLeader'
                      && r.newKind === disposition.streamKind,
                  );
                  return defaultCncStructureIdsForTrack(leader?.newStructureIds, proposalIds);
                }
                return defaultCncStructureIdsForTrack(row.newStructureIds, proposalIds);
              };

              return (
                <article key={row.clientId} className="pf-version-row-card" role="listitem">
                  <div className="pf-version-row-line pf-version-row-line--top pf-version-row-line--single">
                    <div className="pf-vr-cell pf-vr-cell--file pf-vr-cell--grow">
                      <span className="pf-vr-mu">File</span>
                      <span className="pf-version-row-filename pf-version-row-filename--title" title={row.file?.name || undefined}>
                        {row.file?.name ?? 'No file yet'}
                      </span>
                    </div>
                  </div>
                  <div className="pf-version-row-line pf-version-row-line--bottom">
                    <div className="pf-version-row-fields">
                    {showHistorySelect && !projectSplit ? (
                      <div className="pf-vr-cell pf-vr-cell--grow pf-vr-cell--history">
                        <span className="pf-vr-mu">History</span>
                        <select
                          className="pf-tag-select pf-text-input-height pf-vr-input"
                          aria-label="Version history"
                          disabled={uploading}
                          value={
                            historyStreams.some(s => s.id === row.pickStreamId)
                              ? row.pickStreamId
                              : historyStreams[0]?.id || ''
                          }
                          onChange={ev => {
                            const nextId = ev.target.value;
                            const nextStream = historyStreams.find(s => s.id === nextId);
                            const retagged = defaultCncStructureIdsForTrack(
                              nextStream?.structureIds,
                              proposalStructures.map(s => s.id),
                            );
                            setUploadRows(prev =>
                              prev.map(r =>
                                r.clientId === row.clientId && r.rowKind === 'version'
                                  ? {
                                      ...r,
                                      pickStreamId: nextId,
                                      // A different structure track = different CNC scope; follow it.
                                      cncStructureIds:
                                        r.forCnc && isThreeD && retagged.length > 0 ? retagged : r.cncStructureIds,
                                    }
                                  : r,
                              ),
                            );
                          }}
                        >
                          {historyStreams.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.title} ({trackKindShort(s.streamKind)})
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {showHistoryReadonly && !projectSplit ? (
                      <div className="pf-vr-cell pf-vr-cell--grow pf-vr-cell--history">
                        <span className="pf-vr-mu">History</span>
                        <span
                          className="pf-version-row-filename pf-vr-input pf-text-input-height"
                          title={historyStreams[0]?.title}
                          style={{ display: 'flex', alignItems: 'center', paddingInline: '10px' }}
                        >
                          {historyStreams[0]?.title} ({trackKindShort(historyStreams[0]!.streamKind)})
                        </span>
                      </div>
                    ) : null}
                    {showBatchFollow ? (
                      <div className="pf-vr-cell pf-vr-cell--grow pf-vr-cell--history">
                        <span className="pf-vr-mu">Track</span>
                        <span
                          className="pf-version-row-filename pf-vr-input pf-text-input-height"
                          style={{ display: 'flex', alignItems: 'center', paddingInline: '10px' }}
                        >
                          Adds a version to the new {trackKindShort(disposition.streamKind)} track started above (same
                          upload batch).
                        </span>
                      </div>
                    ) : null}
                    {showNewTrackFields ? (
                      <>
                        <div className="pf-vr-cell pf-vr-cell--grow pf-vr-cell--title">
                          <span className="pf-vr-mu">Track title</span>
                          <input
                            type="text"
                            className="pf-text-input pf-text-input-height pf-vr-input"
                            placeholder="New track title"
                            title="Title for this version history"
                            value={row.newTitle}
                            disabled={uploading}
                            onChange={ev =>
                              setUploadRows(prev =>
                                prev.map(r =>
                                  r.clientId === row.clientId && r.rowKind === 'version'
                                    ? { ...r, newTitle: ev.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </div>
                        <div className="pf-vr-cell pf-vr-cell--type">
                          <span className="pf-vr-mu">Type</span>
                          <select
                            className="pf-tag-select pf-text-input-height pf-vr-input"
                            aria-label="Track type"
                            disabled={uploading}
                            value={
                              kindOptions.includes(row.newKind)
                                ? row.newKind
                                : kindOptions[0] ?? 'three_d_html'
                            }
                            onChange={ev =>
                              setUploadRows(prev =>
                                prev.map(r =>
                                  r.clientId === row.clientId && r.rowKind === 'version'
                                    ? {
                                        ...r,
                                        newKind: ev.target.value as ProjectFileStreamKind,
                                      }
                                    : r,
                                ),
                              )
                            }
                          >
                            {VERSION_TRACK_TYPES_ORDERED.filter(k => kindOptions.includes(k)).map(k => (
                              <option key={k} value={k}>
                                {VERSION_TRACK_TYPE_LABEL[k]}
                              </option>
                            ))}
                          </select>
                        </div>
                        {structuresReady ? (
                          <div className="pf-vr-cell pf-vr-cell--structures">
                            <span className="pf-vr-mu" title={pickStructureGroups ? 'Which structure group this file belongs to. Whole project uploads one shared file.' : projectSplit ? 'Whole project uploads one shared file. Comments, markup, and edits stay together on every structure’s track of this type' : 'Which structures this new track covers — leave as Whole project when it covers everything'}>
                              {pickStructureGroups ? 'Structure group' : 'Structures'}
                            </span>
                            <StructureCheckDropdown
                              compact
                              keepOpenOnChange
                              aria-label={pickStructureGroups ? 'Structure group this file belongs to' : 'Structures this file belongs to'}
                              hint={pickStructureGroups ? 'Whole project uploads one shared file for every structure group.' : projectSplit ? 'Whole project uploads one shared file. Every structure’s track of this type opens that same file.' : undefined}
                              structures={proposalStructures}
                              groups={pickStructureGroups ? structureGroups : undefined}
                              value={row.newStructureIds}
                              disabled={uploading}
                              onChange={ids =>
                                setUploadRows(prev =>
                                  prev.map(r =>
                                    r.clientId === row.clientId && r.rowKind === 'version'
                                      ? { ...r, newStructureIds: ids }
                                      : r,
                                  ),
                                )
                              }
                            />
                          </div>
                        ) : (
                          <p className="pf-vr-structures-locked" title={STRUCTURES_LOCKED_HINT}>
                            {STRUCTURES_LOCKED_HINT}
                          </p>
                        )}
                      </>
                    ) : null}
                    {projectSplit && !showNewTrackFields && structuresReady ? (
                      <>
                        <div className="pf-vr-cell pf-vr-cell--type">
                          <span className="pf-vr-mu">Type</span>
                          <select
                            className="pf-tag-select pf-text-input-height pf-vr-input"
                            aria-label="Track type"
                            disabled={uploading}
                            value={
                              kindOptions.includes(row.newKind)
                                ? row.newKind
                                : kindOptions[0] ?? 'three_d_html'
                            }
                            onChange={ev =>
                              setUploadRows(prev =>
                                prev.map(r =>
                                  r.clientId === row.clientId && r.rowKind === 'version'
                                    ? { ...r, newKind: ev.target.value as ProjectFileStreamKind }
                                    : r,
                                ),
                              )
                            }
                          >
                            {VERSION_TRACK_TYPES_ORDERED.filter(k => kindOptions.includes(k)).map(k => (
                              <option key={k} value={k}>
                                {VERSION_TRACK_TYPE_LABEL[k]}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="pf-vr-cell pf-vr-cell--structures">
                          <span className="pf-vr-mu" title="Which structure group this file belongs to. Whole project uploads one shared file.">
                            {pickStructureGroups ? 'Structure group' : 'Structures'}
                          </span>
                          <StructureCheckDropdown
                            compact
                            keepOpenOnChange
                            aria-label={pickStructureGroups ? 'Structure group this file belongs to' : 'Structures this file belongs to'}
                            hint="Whole project uploads one shared file. A structure group adds this file to that group’s track."
                            structures={proposalStructures}
                            groups={pickStructureGroups ? structureGroups : undefined}
                            value={row.newStructureIds}
                            disabled={uploading}
                            onChange={ids =>
                              setUploadRows(prev =>
                                prev.map(r =>
                                  r.clientId === row.clientId && r.rowKind === 'version'
                                    ? { ...r, newStructureIds: ids }
                                    : r,
                                ),
                              )
                            }
                          />
                        </div>
                      </>
                    ) : null}
                    <div className="pf-vr-cell pf-vr-cell--lod">
                      <span className="pf-lod-label">
                        <span className="pf-vr-mu">LOD *</span>
                        <LodHelp />
                      </span>
                      <select
                        className="pf-text-input pf-text-input-height pf-vr-input"
                        aria-label="Level of detail"
                        value={row.detailStage}
                        disabled={uploading}
                        required
                        onChange={ev =>
                          setUploadRows(prev =>
                            prev.map(r =>
                              r.clientId === row.clientId && r.rowKind === 'version'
                                ? { ...r, detailStage: ev.target.value as typeof row.detailStage }
                                : r,
                            ),
                          )
                        }
                      >
                        <option value="">Choose…</option>
                        {PROJECT_FILE_DETAIL_STAGES.map(stage => (
                          <option key={stage} value={stage}>
                            {PROJECT_FILE_DETAIL_STAGE_LABELS[stage]}
                          </option>
                        ))}
                      </select>
                    </div>
                    {isTimberList ? (
                      <div className="pf-vr-cell pf-vr-cell--true-dim">
                        <span className="pf-vr-mu">True dimensions</span>
                        <label className="pf-cnc-toggle">
                          <input
                            type="checkbox"
                            checked={row.trueDimensions}
                            disabled={uploading}
                            onChange={ev =>
                              setUploadRows(prev =>
                                prev.map(r =>
                                  r.clientId === row.clientId && r.rowKind === 'version'
                                    ? { ...r, trueDimensions: ev.target.checked }
                                    : r,
                                ),
                              )
                            }
                          />
                          Cross-section stays as written in the CSV (7.5 stays 7.5). Length is unchanged.
                        </label>
                      </div>
                    ) : null}
                    <div className="pf-vr-cell pf-vr-cell--grow">
                      <span className="pf-vr-mu">Label *</span>
                      <input
                        type="text"
                        className="pf-text-input pf-text-input-height pf-vr-input"
                        placeholder="Rev / issued…"
                        title="Version label"
                        value={row.versionLabel}
                        disabled={uploading}
                        onChange={ev =>
                          setUploadRows(prev =>
                            prev.map(r =>
                              r.clientId === row.clientId && r.rowKind === 'version'
                                ? { ...r, versionLabel: ev.target.value }
                                : r,
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="pf-vr-cell pf-vr-cell--grow">
                      <span className="pf-vr-mu">Note</span>
                      <input
                        type="text"
                        className="pf-text-input pf-text-input-height pf-vr-input"
                        placeholder="Optional"
                        title="Optional note"
                        value={row.versionNote}
                        disabled={uploading}
                        onChange={ev =>
                          setUploadRows(prev =>
                            prev.map(r =>
                              r.clientId === row.clientId && r.rowKind === 'version'
                                ? { ...r, versionNote: ev.target.value }
                                : r,
                            ),
                          )
                        }
                      />
                    </div>
                    {isThreeD ? (
                      <div className="pf-vr-cell pf-vr-cell--cnc">
                        <label className="pf-cnc-toggle">
                          <input
                            type="checkbox"
                            checked={row.forCnc}
                            disabled={uploading}
                            onChange={ev => {
                              const checked = ev.target.checked;
                              const preselect = checked && structuresReady ? cncDefaultStructureIds() : [];
                              setUploadRows(prev =>
                                prev.map(r =>
                                  r.clientId === row.clientId && r.rowKind === 'version'
                                    ? {
                                        ...r,
                                        forCnc: checked,
                                        cncStructureIds: checked
                                          ? r.cncStructureIds.length > 0
                                            ? r.cncStructureIds
                                            : preselect
                                          : [],
                                      }
                                    : r,
                                ),
                              );
                            }}
                          />
                          For CNC
                        </label>
                        {row.forCnc ? (
                          !structuresReady ? (
                            <p className="pf-cnc-empty">{STRUCTURES_LOCKED_HINT}</p>
                          ) : (
                            <StructureCheckDropdown
                              compact
                              hideClear
                              aria-label="Structures this CNC version covers"
                              placeholder="Pick structures…"
                              structures={proposalStructures}
                              value={row.cncStructureIds}
                              disabled={uploading}
                              onChange={ids =>
                                setUploadRows(prev =>
                                  prev.map(r =>
                                    r.clientId === row.clientId && r.rowKind === 'version'
                                      ? { ...r, cncStructureIds: ids }
                                      : r,
                                  ),
                                )
                              }
                            />
                          )
                        ) : null}
                      </div>
                    ) : null}
                    </div>
                    {uploadModalKind === 'version' && visibleCount > 1 ? (
                      <button
                        type="button"
                        className="pf-icon-btn pf-icon-danger pf-version-row-remove pf-vr-cell-remove"
                        title="Remove"
                        aria-label="Remove row"
                        disabled={uploading}
                        onClick={() =>
                          setUploadRows(prev => {
                            const next = prev.filter(r => r.clientId !== row.clientId);
                            const versionLeft = next.filter(r => r.rowKind === 'version');
                            if (versionLeft.length === 0)
                              return [emptyVersionUploadRowUnified(streams, hintStreamId)];
                            return next;
                          })
                        }
                      >
                        <Trash2 size={15} />
                      </button>
                    ) : (
                      <span className="pf-version-row-remove-placeholder pf-vr-cell-remove" aria-hidden />
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <div className="pf-modal-actions">
          <button type="button" className="pf-modal-cancel" disabled={uploading} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="pf-upload-btn pf-upload-btn--accent"
            disabled={uploading}
            onClick={() => void onSubmit()}
          >
            {uploading ? (
              <>
                <Loader2 size={14} className="pf-spin" /> Uploading…
              </>
            ) : (
              <>
                Upload{visibleCount > 1 ? ` (${visibleCount})` : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectUploadModalDialog;
